import { saveAs } from 'file-saver';
import moment from 'moment-timezone';
import save from 'save-file';

import { Config } from '../config';
import { HomographyFilenames } from '../constants';
import {
  Device,
  DeviceHwVersion,
  DeviceOTA,
  JiraTicketCreation,
  OTAFileV2,
  OTARepository,
  OTAResponse,
  RepoImage,
  RepoPackage,
} from '../interfaces';
import { CalculateHomographyResponse, CalibrationPoint, CalibrationType, HomographyRequest } from '../interfaces/homography';
import { computeHomographyMatrix } from '../utils/calculateHomography';
import { HttpService } from './http.service';

interface DeviceHwPayload {
  version_name: string;
}

const path = `${Config.API}/api/v1/devices`;
const pathV2 = `${Config.API}/api/v2/devices`;

export abstract class DevicesService {
  public static getAll = (): Promise<Device[]> => {
    return HttpService.fetchAuthorized(path).then((res) => {
      if (!res) {
        return [];
      }

      res.sort((a: Device, b: Device) => a.id.localeCompare(b.id));
      return res;
    });
  };

  public static get = (id: Device['id']): Promise<Device> => {
    return HttpService.fetchAuthorized(`${path}/${id}`).then((res) => res);
  };

  public static create = (body: Device): Promise<any> => {
    return HttpService.fetchAuthorized(
      path,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
      null,
      true,
    ).then((res) => res);
  };

  public static update = (id: string, body: Partial<Device>): Promise<any> => {
    return HttpService.fetchAuthorized(`${path}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }).then((res) => res);
  };

  public static delete = (id: string): Promise<any> => {
    return HttpService.fetchAuthorized(`${path}/${id}`, {
      method: 'DELETE',
    }).then((res) => res);
  };

  public static getOTAv2 = (id: string): Promise<DeviceOTA> => {
    return HttpService.fetchAuthorized(`${pathV2}/${id}/ota`, {
      cache: 'no-store',
    }).then((res) => res);
  };

  public static getDeviceVPNIp = (deviceId: string): Promise<string> => {
    return HttpService.fetchAuthorized(`${path}/${deviceId}/vpn/ip`).then((res) => res?.address);
  };

  public static getOTARepo = (): Promise<OTARepository> => {
    return HttpService.fetchAuthorized(`${Config.API}/api/v1/config/ota/repository`).then((res) => {
      if (!res) {
        return { images: [], packages: [] };
      }

      res.images.sort((a: RepoImage, b: RepoImage) => a.repository.localeCompare(b.repository));
      res.packages.sort((a: RepoPackage, b: RepoPackage) => a.name.localeCompare(b.name));

      return res;
    });
  };

  public static calibrateHomography = (
    deviceId: string,
    calculatedHomography: Float32Array,
    calibrationType: CalibrationType,
  ): Promise<OTAResponse> => {
    const varName = calibrationType === CalibrationType.MAP ? 'cam_gps_homography' : 'homography';
    const homographyFileContents = `%YAML:1.0
    ---
    ${varName}: !!opencv-matrix
       rows: 3
       cols: 3
       dt: f
       data: [ ${calculatedHomography.join(', ')} ]
    `;

    const filename = HomographyFilenames[calibrationType];

    const file = new File([homographyFileContents], filename);

    const idToPayload = filename.split('.')[0]; // id without .kml extension

    return DevicesService.createOtaFileOnDevice(
      deviceId,
      {
        key: filename,
        destination: filename,
        id: idToPayload,
      },
      file,
    );
  };

  public static calculateHomography = (deviceId: string, data: HomographyRequest): Promise<CalculateHomographyResponse> => {
    const lprData = data.imagePairs.find((d) => d.calibrationType === CalibrationType.LPR);
    const nightLprData = data.imagePairs.find((d) => d.calibrationType === CalibrationType.NIGHTLPR);
    const mapData = data.imagePairs.find((d) => d.calibrationType === CalibrationType.MAP);

    const calibrationPointToPoint = (points: CalibrationPoint): number[] => {
      return [points[1][0], points[1][1]];
    };

    const lprHomo =
      lprData &&
      computeHomographyMatrix(
        lprData.imagePairs.imageOne.points.map(calibrationPointToPoint),
        lprData.imagePairs.imageTwo.points.map(calibrationPointToPoint),
      );

    const nightLprHomo =
      nightLprData &&
      computeHomographyMatrix(
        nightLprData.imagePairs.imageOne.points.map(calibrationPointToPoint),
        nightLprData.imagePairs.imageTwo.points.map(calibrationPointToPoint),
      );

    const mapHomo =
      mapData &&
      mapData.imagePairs.imageOne.points.map(calibrationPointToPoint).length &&
      computeHomographyMatrix(
        mapData.imagePairs.imageOne.points.map(calibrationPointToPoint),
        mapData.imagePairs.imageTwo.points.map(calibrationPointToPoint),
      );

    const homographyCache = {};

    if (!homographyCache[deviceId]) {
      homographyCache[deviceId] = {};
    }

    if (lprData) {
      homographyCache[deviceId][CalibrationType.LPR] = new Float32Array(lprHomo.flat());
    }

    if (nightLprData) {
      homographyCache[deviceId][CalibrationType.NIGHTLPR] = new Float32Array(nightLprHomo.flat());
    }

    if (mapData && mapHomo) {
      homographyCache[deviceId][CalibrationType.MAP] = new Float32Array(mapHomo.flat());
    }

    const res: CalculateHomographyResponse = {
      transformMatrices: [
        homographyCache[deviceId][CalibrationType.LPR]
          ? {
              calibrationType: CalibrationType.LPR,
              transformMatrix: homographyCache[deviceId][CalibrationType.LPR],
            }
          : null,
        homographyCache[deviceId][CalibrationType.NIGHTLPR]
          ? {
              calibrationType: CalibrationType.NIGHTLPR,
              transformMatrix: homographyCache[deviceId][CalibrationType.NIGHTLPR],
            }
          : null,
        homographyCache[deviceId][CalibrationType.MAP]
          ? {
              calibrationType: CalibrationType.MAP,
              transformMatrix: homographyCache[deviceId][CalibrationType.MAP],
            }
          : null,
      ].filter(Boolean),
    };

    return Promise.resolve(res);
  };

  public static getIAT = (id: string, date?: string) => {
    const queryString = `date=${moment(date || new Date()).format('YYYY-MM-DD')}`;
    return HttpService.fetchAuthorized(
      `${path}/${id}/iat?${queryString}`,
      {
        method: 'GET',
      },
      null,
      true,
    ).then((res) => {
      save(res, `iat-report-${id}.pdf`);
    });
  };

  public static migrateDevice = (id: string, targetFleet: string, targetOtaGroupId?: number): Promise<any> => {
    return HttpService.fetchAuthorized(
      `${pathV2}/${id}/migrate`,
      {
        method: 'PUT',
        body: JSON.stringify({ fleet_uuid: targetFleet, ota_group_id: targetOtaGroupId }),
      },
      null,
      false,
    ).then(
      (res) => Promise.resolve(res),
      (rej) => Promise.reject(rej),
    );
  };

  public static getDeviceFiles = (id: string): Promise<OTAFileV2[]> => {
    return HttpService.fetchAuthorized(`${pathV2}/${id}/files`).then((res) => res);
  };

  public static createOtaFileOnDevice = (deviceId: string, fileData: Partial<OTAFileV2>, file: File, metadata = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('key', fileData.key);
    formData.append('destination', fileData.destination);
    formData.append('id', fileData.id);
    formData.append('category', fileData.category);
    Object.entries(metadata).forEach(([metadataKey, value]) => {
      formData.append(metadataKey, value);
    });

    return HttpService.fetchAuthorized(`${Config.API}/api/v2/devices/${deviceId}/files/upload`, {
      method: 'POST',
      body: formData,
    }).then((res) => {
      return res;
    });
  };

  public static getDeviceFileContent = (deviceId: string, file: OTAFileV2) => {
    const url = `${pathV2}/${deviceId}/files/${file.device_file_id}/content`;
    return HttpService.fetchAuthorizedFileWithFleet(url).then((res) => res.text());
  };

  public static downloadDeviceFile = (deviceId: string, file: OTAFileV2) => {
    const url = `${pathV2}/${deviceId}/files/${file.device_file_id}/content`;
    return HttpService.fetchAuthorizedFileWithFleet(url).then((res) => {
      if (res) {
        res.blob().then((blob) => {
          saveAs(blob, file.key);
        });
      }
    });
  };

  public static deleteOtaFile = (deviceId: string, fileId: number) => {
    return HttpService.fetchAuthorized(`${pathV2}/${deviceId}/files/${fileId}`, {
      method: 'DELETE',
    }).then((res) => {
      return res;
    });
  };

  public static updateDeviceFile = (deviceId: string, file: OTAFileV2) => {
    return HttpService.fetchAuthorized(`${pathV2}/${deviceId}/files/${file.device_file_id}`, {
      method: 'PUT',
      body: JSON.stringify(file),
    }).then((res) => {
      return res;
    });
  };

  public static addJiraTicketToDevice = (deviceId: string, payload: JiraTicketCreation) => {
    return HttpService.fetchAuthorized(`${path}/${deviceId}/jira`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }).then((res) => {
      return res;
    });
  };

  public static getDeviceHwVersions = (): Promise<DeviceHwVersion[]> => {
    return HttpService.fetchAuthorized(`${path}/device_hw_versions`).then((res) => res);
  };

  public static createDeviceHwVersion = (payload: DeviceHwPayload): Promise<any> => {
    return HttpService.fetchAuthorized(
      `${path}/device_hw_versions`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      null,
      true,
    ).then((res) => res);
  };
}
